<script>
  import {
    scaleLinear,
    scaleQuantize,
    scaleSequential,
    scaleThreshold,
  } from 'd3-scale';
  import { arc as d3arc } from 'd3-shape';
  import { cumsum } from 'd3-array';
  import { hsl } from 'd3-color';
  import { interpolateInferno, schemeRdYlBu } from 'd3-scale-chromatic';
  export let team;
  export let players;


  const angleGlobalOffset = 0.0 * Math.PI;
  export let width = 600;
  let height = 600;

  const angleMap = {
    'rookie': (1 / 6) * 2 * Math.PI,
    'never-playoffs': (2 / 6) * 2 * Math.PI,
    'played-playoffs': (3 / 6) * 2 * Math.PI,
    'won-playoff-series': (4 / 6) * 2 * Math.PI,
    'played-finals': (5 / 6) * 2 * Math.PI,
    'won-finals': (6 / 6) * 2 * Math.PI,
  };
  const labelMap = {
    'rookie': 'Rookies',
    'never-playoffs': 'Regular Season',
    'played-playoffs': 'Made Playoffs',
    'won-playoff-series': 'Won a Series',
    'played-finals': 'Made Finals',
    'won-finals': 'Champions',
  };
  

  const margin = { top: 10, right: 10, bottom: 10, left: 10 };
  const innerRadius = 20;
  const ringMargin = 5;
  const refRingMargin = ringMargin * 4;

  function darkerColor(color) {
    let c = hsl(color);
    c.l -= 0.05;
    c.s -= 0.05;
    return c.toString();
  }

  function lighterColor(color) {
    let c = hsl(color);
    c.l += 0.2;
    c.s += 0.2;
    return c.toString();
  }

  function glowColor(color, factor = 1) {
    let c = hsl(color);
    c.l += factor * 0.02;
    c.s += factor * 0.02;
    return c.toString();
  }
  // based on games played
  let thicknessScale = scaleLinear().domain([0, 1000]).range([2, 10]);
  const maxThickness = thicknessScale.range()[1];
  // thicknessScale = (d) => 4;
  const thicknessKey = 'career_gp';
  const colorScale = scaleThreshold()
    .domain([0.5, 0.6]) // 60%+ is roughly 85th percentil, 50% is roughly 54th percentile
    .range(['#2d3467', '#3c9245', '#f9ce1a'])
    .range(['#0d478a', '#1c9a8b', '#f9ce1a'])
    .range(['#0d478a', '#9e8667', '#f9ce1a'])
    .range(['#992B3D', '#0d478a', '#24C79E'])
    .range(['#cc002f', '#df7f45', '#eecb61'])
    .range(['#912d2c', '#d88130', '#fbe254'])
    .range(['#edf8b1', '#7fcdbb', '#2c7fb8'])
    .range(['#fff58d', '#5bb68f', '#00649f'].reverse())
  const noGpColor = '#6b7280';

  const colorScaleOuter = colorScale
    .copy()
    .range(colorScale.range().map(darkerColor));

  // .range(['#AD813F', '#d1d5db', '#f9ce1a']) // "medals"
  // .range(['#ffeda0', '#feb24c', '#f03b20'])

  var arc = d3arc();

  const colorScale2 = scaleSequential((t) => interpolateInferno(t * 0.8 + 0.2));
  window.c = colorScale;
  //.range(schemeRdYlBu[9]);
  // .range(['blue', 'gray', 'red', 'purple', 'lime']);
  const colorKey = 'career_win_pct';
  const refIndex = 4;

  // let sortedPlayers = players.slice().filter(d => d.career_gp > 0); //players.slice().reverse();
  let sortedPlayers = players

  const thickness = sortedPlayers.map((d) =>
    Math.round(thicknessScale(d[thicknessKey]))
  );
  const cumulativeThickness = cumsum(sortedPlayers, (d) =>
    Math.round(thicknessScale(d[thicknessKey]))
  );

  const refLineLength =
    cumulativeThickness[cumulativeThickness.length - 1] +
    ringMargin * (players.length - 1) +
    innerRadius +
    refRingMargin +
    10;
  height = refLineLength * 2 + margin.top + margin.bottom;
  width = height;
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const refRingRadius =
    cumulativeThickness[refIndex] +
    ringMargin * refIndex +
    innerRadius +
    refRingMargin / 2 +
    ringMargin / 2;
  // thickness[refIndex] / 2;

  let gradientId = `gradient-${team}`;
  let gradientId2 = `gradient-${team}2`;
  const highlightWidth = 2;


  const innerRadii = sortedPlayers.map((d, i) => innerRadius +
                  (cumulativeThickness[i - 1] ?? 0) +
                  ringMargin * i +
                  (i > refIndex ? refRingMargin : 0))
  const outerRadii = sortedPlayers.map((d, i) => innerRadius +
                  cumulativeThickness[i] +
                  ringMargin * i +
                  
                  (i > refIndex ? refRingMargin : 0))

  const endAngles = sortedPlayers.map((player, i) => (angleMap[player.career_gp === 0 ? 'rookie' : player.playoff_status ?? 'never-playoffs']));

  
  const refPoints = Object.keys(angleMap).map((key, i) => ({
    key,
    angle: angleMap[key],
    r: refLineLength - 8,
    color: ['red', 'green', 'blue', 'cyan', 'magenta', 'yellow', 'black'][i],
    x: Math.cos(angleMap[key] - angleGlobalOffset - Math.PI * 0.5) * (refLineLength - 8),
    y: Math.sin(angleMap[key] - angleGlobalOffset - Math.PI * 0.5) * (refLineLength - 8),
  }))
  for (let i= 0; i < refPoints.length; ++i) {
    refPoints[i].prev = refPoints[(i - 1 + refPoints.length) % refPoints.length];
    refPoints[i].next = refPoints[(i + 1) % refPoints.length];
  }

</script>

<style>
  h5 {
    margin: 0;
  }
  svg {
    overflow: visible;
  }
</style>

<div>
  <svg {width} {height}>
    <defs>
      <radialGradient id={gradientId}>
        <stop offset="5%" stop-color="hsl(229deg 69% 20%)" />
        <stop offset="100%" stop-color="hsla(229deg, 69%, 20%, 0)" />
      </radialGradient>
      <linearGradient id={gradientId2} gradientTransform="rotate(-20)">
        <stop offset="0%" stop-color="hsla(229deg, 69%, 20%, 0)" />
        <stop offset="100%" stop-color="hsla(229deg, 69%, 30%, 0.8)" />
      </linearGradient>
    </defs>
    <g transform="translate({margin.left} {margin.top})">
      <g transform="translate({innerWidth / 2} {innerHeight / 2})">
        <circle
          r={width * 0.2}
          xfill="hsl(229deg 69% 20%)"
          fill="url(#{gradientId})" />
        <circle
          r={refRingRadius}
          xfill="hsl(229deg 69% 20%)"
          fill="none"
          stroke-width={1.5}
          stroke-dasharray="3 2"
          stroke="hsl(229deg 69% 35%)"
          strokxe="lime" />
        <g style="opacity: 0.5">
          <line
          x1={0} y1={0}
          x2={Math.cos(angleGlobalOffset - Math.PI * 0.5 + angleMap['rookie']) * refLineLength}
          y2={Math.sin(angleGlobalOffset - Math.PI * 0.5 + angleMap['rookie']) * refLineLength}
          stroke="#4b5563" />
          <line
            x1={0} y1={0}
            x2={Math.cos(angleGlobalOffset - Math.PI * 0.5 + angleMap['never-playoffs']) * refLineLength}
            y2={Math.sin(angleGlobalOffset - Math.PI * 0.5 + angleMap['never-playoffs']) * refLineLength}
            stroke="#4b5563" />
          <line
            x1={0} y1={0}
            x2={Math.cos(angleGlobalOffset - Math.PI * 0.5 + angleMap['played-playoffs']) * refLineLength}
            y2={Math.sin(angleGlobalOffset - Math.PI * 0.5 + angleMap['played-playoffs']) * refLineLength}
            stroke="#4b5563" />
          <line
            x1={0} y1={0}
            x2={Math.cos(angleGlobalOffset - Math.PI * 0.5 + angleMap['won-playoff-series']) * refLineLength}
            y2={Math.sin(angleGlobalOffset - Math.PI * 0.5 + angleMap['won-playoff-series']) * refLineLength}
            stroke="#4b5563" />
          <line
            x1={0} y1={0}
            x2={Math.cos(angleGlobalOffset - Math.PI * 0.5 + angleMap['played-finals']) * refLineLength}
            y2={Math.sin(angleGlobalOffset - Math.PI * 0.5 + angleMap['played-finals']) * refLineLength}
            stroke="#4b5563" />
            <!-- <line
            x1={0}
            x2={Math.cos(angleGlobalOffset - Math.PI * 0.5 + angleMap['won-finals']) * refLineLength}
            y1={0}
            y2={Math.sin(angleGlobalOffset - Math.PI * 0.5 + angleMap['won-finals']) * refLineLength}
            stroke="#4b5563" /> -->
        </g>
        <g>
          <path
              d={arc({
                innerRadius: 0,
                outerRadius:
                  refLineLength - 10,
                startAngle: angleMap['played-finals'] + angleGlobalOffset,
                endAngle: angleMap['won-finals'] + angleGlobalOffset,
                
              })}
              fill="url(#{gradientId2})"
              fill-opacity={1} />
        </g>
        {#each sortedPlayers as player, i}
          <g style={`opacity: ${i >= 5 ? 1 : 1}`}>
            <path
              d={arc({
                innerRadius: innerRadii[i],
                outerRadius: outerRadii[i],
                startAngle: angleGlobalOffset,
                endAngle: endAngles[i] +  angleGlobalOffset,
              })}
              fill={player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey])}
              fill-opacity={1} />
              <!-- highlights-->
            <path
              d={arc({
                innerRadius:innerRadii[i],
                outerRadius: outerRadii[i],
                startAngle: endAngles[i] + angleGlobalOffset - Math.atan(3 / (outerRadii[i])),
                endAngle: endAngles[i] + angleGlobalOffset,
              })}
              fill={glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 2)}
              fill-opacity={1} />
            <path
              d={arc({
                innerRadius:innerRadii[i],
                outerRadius: outerRadii[i],
                startAngle: endAngles[i] + angleGlobalOffset - Math.atan(2 / (outerRadii[i])),
                endAngle: endAngles[i] + angleGlobalOffset,
              })}
              fill={glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 6)}
              fill-opacity={1} />
              <path
              d={arc({
                innerRadius:innerRadii[i],
                outerRadius: outerRadii[i],
                startAngle: endAngles[i] + angleGlobalOffset - Math.atan(1 / (outerRadii[i])),
                endAngle: endAngles[i] + angleGlobalOffset,
              })}
              fill={glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 10)}
              fill-opacity={1} />

              <!-- champion glow-->
              {#if player.playoff_status === 'won-finals'}
              <path
                d={arc({
                  innerRadius:innerRadii[i] + thickness[i] * 0.0,
                  outerRadius: outerRadii[i] - thickness[i] * 0.0,
                  startAngle: angleGlobalOffset,
                  endAngle: endAngles[i] + angleGlobalOffset,
                })}
                fill={((glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), -16)))} />
              <path
                d={arc({
                  innerRadius:innerRadii[i] - 2,
                  outerRadius: outerRadii[i] + 2,
                  startAngle: angleGlobalOffset,
                  endAngle: endAngles[i] + angleGlobalOffset,
                })}
                fill={((glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 0)))} 
                fill-opacity={0.2}/>
                
              <path
                d={arc({
                  innerRadius:innerRadii[i] + thickness[i] * 0.1,
                  outerRadius: outerRadii[i] - thickness[i] * 0.1,
                  startAngle: angleGlobalOffset,
                  endAngle: endAngles[i] + angleGlobalOffset,
                })}
                fill={((glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 2)))} />
              <path
                d={arc({
                  innerRadius:innerRadii[i] + thickness[i] * 0.2,
                  outerRadius: outerRadii[i] - thickness[i] * 0.2,
                  startAngle: angleGlobalOffset,
                  endAngle: endAngles[i] + angleGlobalOffset,
                })}
                fill={((glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 4)))} />
              <path
                d={arc({
                  innerRadius:innerRadii[i] + thickness[i] * 0.3,
                  outerRadius: outerRadii[i] - thickness[i] * 0.3,
                  startAngle: angleGlobalOffset,
                  endAngle: endAngles[i] + angleGlobalOffset,
                })}
                fill={((glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 6)))} />
              <path
                d={arc({
                  innerRadius:innerRadii[i] + thickness[i] * 0.4,
                  outerRadius: outerRadii[i] - thickness[i] * 0.4,
                  startAngle: angleGlobalOffset,
                  endAngle: endAngles[i] + angleGlobalOffset,
                })}
                fill={((glowColor(player.career_gp === 0 ? noGpColor : i >= 5 ? colorScaleOuter(player[colorKey]) : colorScale(player[colorKey]), 8)))} />
              {/if}
            
          </g>
        {/each}

        <g style="opacity: 0.5">
          <line
            x1={0}
            x2={Math.cos(angleGlobalOffset - Math.PI * 0.5) * refLineLength}
            y1={0}
            y2={Math.sin(angleGlobalOffset - Math.PI * 0.5) * refLineLength}
            stroke="#9ca3af"
            stroke-width={2} />
            
        </g>
      </g>


      <g transform="translate({innerWidth / 2} {innerHeight / 2})">
      <!-- <g>
        <path d="M0      -205    A 205 205 0 0 0 -177.5 -102.5" fill="none" stroke="lime" id="test" transform="translate(0 5)"/>
        <path d="M-177.5 -102.5  A 205 205 0 0 1 0      -205" fill="none" stroke="yellow" id="test2"/>
        <text text-anchor="middle" fill="white">
          <textPath href={`#test`}  startOffset="50%">testing</textPath>
          
        </text>
        <text text-anchor="middle" fill="white" dy={0}>
          <textPath href={`#test2`}  startOffset="50%">22222</textPath>
        </text>

      </g> -->

      <g>

        {#each refPoints as d,i}
        <defs>
        <!-- <circle r={5} fill="tomato" cx={d.x} cy={d.y} /> -->
        {#if i == 0 || i === 4 || i === 5}
          <path id={`text-path-${team}-${d.key}`} d={`M${d.prev.x} ${d.prev.y} A ${d.r} ${d.r} 0 0 1 ${d.x} ${d.y}`} stroke="{d.color}" fill="none"/>
        {:else}
          <path id={`text-path-${team}-${d.key}`} d={`M${d.x} ${d.y} A ${d.r} ${d.r} 0 0 0 ${d.prev.x} ${d.prev.y}`} stroke="{d.color}" fill="none"/>
        {/if}
        </defs>
      <g transform="">
      <text text-anchor="middle" class="milestone-label" style="font-size: 10px; letter-spacing: 1px; text-transform: uppercase" fill="currentColor" fill-opacity="0.6" dy={i === 0 || i === 4 || i === 5 ? -5 : 15}>
        <textPath side="left" href={`#text-path-${team}-${d.key}`}  startOffset="50%">{labelMap[d.key]}</textPath>
      </text>
    </g>
      {/each}

      </g>
    </g>
    </g>
  </svg>
  <!-- 
  <pre>
    {#each players as player, i}
      {player.career_gp} {player.season_mp}
    {'\n'}
    {/each}
  </pre> -->
</div>
